import React from "react";
import { Dialog } from "../../../ui/components/Dialog";
import { Button } from "../../../ui/components/Button";
import { Table } from "../../../ui/components/Table";
import { FeatherCheckCircle } from "@subframe/core";
import { ReviewRequestDTO } from "../../data/api/reviewsApi";
import { ChangeDetails } from "../../utils/reviewWorkflow";

// Re-export for backward compatibility
export type { ChangeDetails };

interface ApprovedChangesModalProps {
  open: boolean;
  review: ReviewRequestDTO;
  onApply: (changes: ChangeDetails) => void;
  onClose: () => void;
  isApplying?: boolean;
  isPostedToZoho?: boolean;
  documentType?: "invoice" | "bill" | "credit_note" | "debit_note";
  documentReferenceNumber?: string; // Reference number to display (e.g., invoice number, credit note reference number)
}

/**
 * ApprovedChangesModal Component
 *
 * Shows list of approved changes from change_details
 * Displays line items with GL changes (old → new)
 * "Apply Changes" button to populate form
 */
export const ApprovedChangesModal: React.FC<ApprovedChangesModalProps> = ({
  open,
  review,
  onApply,
  onClose,
  isApplying = false,
  isPostedToZoho = false,
  documentType = "invoice",
  documentReferenceNumber,
}) => {
  const changeDetails = review.change_details as ChangeDetails | undefined;

  // Extract reference number from raw_json if not provided as prop
  const referenceNumber =
    documentReferenceNumber ||
    (changeDetails?.raw_json?.reference_number as string | undefined) ||
    (changeDetails?.raw_json?.invoice_number as string | undefined) ||
    "";

  // Format document type for display (convert snake_case to Title Case)
  const documentTypeDisplay: string =
    documentType === "credit_note"
      ? "Credit Note"
      : documentType === "debit_note"
      ? "Debit Note"
      : documentType === "invoice"
      ? "Invoice"
      : documentType === "bill"
      ? "Bill"
      : String(documentType)
          .split("_")
          .map(
            (word: string) =>
              word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
          )
          .join(" ");

  // raw_json is for applying changes only, not for display - it's already filtered out

  if (!changeDetails) {
    return null;
  }

  const handleApply = () => {
    // Just call onApply - parent will handle closing and navigation
    // The loader will show via isApplying prop
    onApply(changeDetails);
  };

  // Prevent closing when clicking outside or pressing ESC while applying
  // Only allow closing when parent explicitly closes it (after navigation)
  const handleOpenChange = (newOpen: boolean) => {
    // If trying to close while applying, prevent closing
    if (!newOpen && isApplying) {
      // Prevent closing - keep modal open with loader
      return;
    }
    // If not applying, allow parent to close (for navigation)
    if (!newOpen && !isApplying) {
      onClose();
    }
  };

  // Helper function to format snake_case values
  const formatValue = (val: string): string => {
    if (!val || val === "empty") return val;
    // Convert to string if it's not already
    const strVal = String(val);
    // If it contains underscores and looks like snake_case, format it
    if (
      strVal.includes("_") &&
      !strVal.startsWith("[") &&
      !strVal.startsWith("{")
    ) {
      return strVal
        .split("_")
        .map(
          (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        )
        .join(" ");
    }
    return strVal;
  };

  // Helper function to format field names
  const formatFieldName = (fieldName: string): string => {
    const fieldLower = fieldName.toLowerCase();

    // Special field name replacements based on document type
    if (
      fieldLower === "gst no" ||
      fieldLower === "gst_no" ||
      fieldLower.replace(/\s+/g, "_") === "gst_no"
    ) {
      // For invoices: "Customer GSTIN", for bills: "Vendor GSTIN"
      return documentType === "invoice" ? "Customer GSTIN" : "Vendor GSTIN";
    } else if (
      fieldLower === "customer gstin" ||
      fieldLower === "customer_gstin"
    ) {
      return "Customer GSTIN";
    } else if (fieldLower === "vendor gstin" || fieldLower === "vendor_gstin") {
      return "Vendor GSTIN";
    } else if (
      fieldLower === "gst treatment" ||
      fieldLower === "gst_treatment"
    ) {
      return "GST Treatment";
    }

    // Format snake_case field names to readable format
    if (fieldName.includes("_")) {
      return fieldName
        .split("_")
        .map(
          (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        )
        .join(" ");
    }

    // Capitalize first letter of each word
    return fieldName
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  // ID fields to hide from display
  const idFieldsToHide = [
    "customer_id",
    "vendor_id",
    "customerid",
    "vendorid",
    "tax_id",
    "taxid",
    "account_id",
    "accountid",
    "gl_id",
    "glid",
    "tds_tax_id",
    "tds_taxid",
    "id",
    "_id",
  ];

  // Filter out ID fields and format field changes
  const fieldChanges = (changeDetails.field_changes || [])
    .filter((change) => {
      const fieldLower = change.field.toLowerCase();
      // Skip all ID fields
      if (
        idFieldsToHide.some((idField) => fieldLower.includes(idField)) ||
        fieldLower.endsWith("_id") ||
        fieldLower.endsWith("id")
      ) {
        return false;
      }
      // Skip raw line_items[${index}] fields
      if (
        change.field.startsWith("line_items[") &&
        change.field.endsWith("]")
      ) {
        return false;
      }
      // Skip "Line Items" field if it contains raw JSON arrays
      const isJsonArray = (val: string) => {
        try {
          const parsed = JSON.parse(val);
          return (
            Array.isArray(parsed) &&
            parsed.length > 0 &&
            typeof parsed[0] === "object"
          );
        } catch {
          return false;
        }
      };
      if (
        fieldLower === "line items" ||
        fieldLower === "line_items" ||
        (change.old &&
          isJsonArray(change.old) &&
          change.new &&
          isJsonArray(change.new))
      ) {
        return false;
      }
      return true;
    })
    .map((change) => ({
      ...change,
      field: formatFieldName(change.field),
      old: formatValue(change.old),
      new: formatValue(change.new),
    }));

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <Dialog.Content className="min-w-[600px] max-w-[800px]">
        <div className="flex w-full flex-col items-start gap-6 bg-white px-8 py-8">
          {/* Header */}
          <div className="flex w-full items-center justify-between">
            <div className="flex items-center gap-3">
              <FeatherCheckCircle className="h-6 w-6 text-success-600" />
              <span className="text-heading-2 font-heading-2 text-default-font">
                Approved Changes
              </span>
            </div>
            {/* Close icon removed - user must apply changes */}
          </div>

          {/* Review Info - First */}
          <div className="flex w-full flex-col items-start gap-2 rounded-md bg-neutral-50 px-4 py-3 border border-solid border-neutral-200">
            <span className="text-body-bold font-body-bold text-default-font">
              Review Information
            </span>
            <div className="flex flex-col items-start gap-1">
              <span className="text-body font-body text-subtext-color">
                <strong>Document Type:</strong> {documentTypeDisplay}
                {referenceNumber && ` - ${referenceNumber}`}
              </span>
              <span className="text-body font-body text-subtext-color">
                <strong>Status:</strong>{" "}
                {review.status.charAt(0).toUpperCase() +
                  review.status.slice(1).toLowerCase()}
              </span>
              {review.review_comments && (
                <span className="text-body font-body text-subtext-color">
                  <strong>Review Comments:</strong> {review.review_comments}
                </span>
              )}
              {review.reviewer_name && (
                <span className="text-body font-body text-subtext-color">
                  <strong>
                    {review.status === "approved"
                      ? "Approved by"
                      : "Rejected by"}
                    :
                  </strong>{" "}
                  {review.reviewer_name}
                  {review.reviewer_role && ` (${review.reviewer_role})`}
                </span>
              )}
              {review.reviewed_at && (
                <span className="text-body font-body text-subtext-color">
                  <strong>
                    {review.status === "approved" ? "Approved" : "Rejected"} on:
                  </strong>{" "}
                  {new Date(review.reviewed_at).toLocaleString()}
                </span>
              )}
            </div>
          </div>

          {/* Reason - Second */}
          {changeDetails.reason && (
            <div className="flex w-full flex-col items-start gap-2 rounded-md bg-neutral-50 px-4 py-3 border border-solid border-neutral-200">
              <span className="text-body-bold font-body-bold text-default-font">
                Reason
              </span>
              <span className="text-body font-body text-subtext-color">
                {changeDetails.reason}
              </span>
            </div>
          )}

          {/* Change Summary - Third */}
          {changeDetails.old_value &&
            changeDetails.new_value &&
            changeDetails.old_value !== changeDetails.new_value && (
              <div className="flex w-full flex-col items-start gap-2 rounded-md bg-brand-50 px-4 py-3 border border-solid border-brand-200">
                <span className="text-body-bold font-body-bold text-brand-700">
                  Change Summary
                </span>
                <div className="flex flex-col items-start gap-1">
                  <span className="text-body font-body text-brand-700">
                    <strong>Old Value:</strong> {changeDetails.old_value}
                  </span>
                  <span className="text-body font-body text-brand-700">
                    <strong>New Value:</strong> {changeDetails.new_value}
                  </span>
                </div>
              </div>
            )}

          {/* Field Changes Table - Always show if field_changes exists */}
          {changeDetails.field_changes &&
            changeDetails.field_changes.length > 0 && (
              <div className="flex w-full flex-col items-start gap-4">
                <span className="text-body-bold font-body-bold text-default-font">
                  Changes Summary
                </span>
                {fieldChanges.length > 0 ? (
                  <Table
                    header={
                      <Table.HeaderRow>
                        <Table.HeaderCell>Field</Table.HeaderCell>
                        <Table.HeaderCell>Old Value</Table.HeaderCell>
                        <Table.HeaderCell>New Value</Table.HeaderCell>
                      </Table.HeaderRow>
                    }
                  >
                    {fieldChanges.map((change, index) => (
                      <Table.Row key={index}>
                        <Table.Cell>
                          <span className="text-body-bold font-body-bold text-default-font">
                            {change.field}
                          </span>
                        </Table.Cell>
                        <Table.Cell>
                          <span className="text-body font-body text-neutral-500">
                            {change.old}
                          </span>
                        </Table.Cell>
                        <Table.Cell>
                          <span className="text-body font-body text-brand-600">
                            {change.new}
                          </span>
                        </Table.Cell>
                      </Table.Row>
                    ))}
                  </Table>
                ) : (
                  <div className="flex w-full items-center justify-center rounded-md border border-solid border-neutral-200 bg-neutral-50 px-4 py-3">
                    <span className="text-body font-body text-subtext-color">
                      Changes are primarily in line items (quantity and rate
                      updates). These will be applied when you click "Apply
                      Changes".
                    </span>
                  </div>
                )}
              </div>
            )}

          {/* Legacy Field Changes Table - Keep for backward compatibility */}
          {(!changeDetails.field_changes ||
            changeDetails.field_changes.length === 0) &&
            fieldChanges.length > 0 && (
              <div className="flex w-full flex-col items-start gap-4">
                <span className="text-body-bold font-body-bold text-default-font">
                  Field Changes
                </span>
                <Table
                  header={
                    <Table.HeaderRow>
                      <Table.HeaderCell>Field</Table.HeaderCell>
                      <Table.HeaderCell>Old Value</Table.HeaderCell>
                      <Table.HeaderCell>New Value</Table.HeaderCell>
                    </Table.HeaderRow>
                  }
                >
                  {fieldChanges.map((change, index) => (
                    <Table.Row key={index}>
                      <Table.Cell>
                        <span className="text-body-bold font-body-bold text-default-font">
                          {change.field}
                        </span>
                      </Table.Cell>
                      <Table.Cell>
                        <span className="text-body font-body text-neutral-500">
                          {change.old}
                        </span>
                      </Table.Cell>
                      <Table.Cell>
                        <span className="text-body font-body text-brand-600">
                          {change.new}
                        </span>
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table>
              </div>
            )}

          {/* Action Buttons */}
          <div className="flex w-full items-center justify-end gap-2 border-t border-solid border-neutral-border pt-6">
            {/* Cancel button removed - user must apply changes */}
            <Button
              variant="brand-primary"
              onClick={handleApply}
              disabled={isApplying}
            >
              {isApplying
                ? isPostedToZoho
                  ? `Updating ${documentTypeDisplay}...`
                  : "Applying Changes..."
                : isPostedToZoho
                ? `Approve and Update ${documentTypeDisplay}`
                : "Apply Changes"}
            </Button>
          </div>
        </div>
      </Dialog.Content>
    </Dialog>
  );
};
